import { useEffect } from "react";
import type { LazyQueryExecFunction } from "@apollo/client";
import type {
  GetVenueReservationBoundsQueryInput,
  GetVenueReservationBoundsQueryResponse,
} from "./queries/GetVenueReservationBoundsQuery";
import type { ReservationSearch, ReservationsBucket } from "./reservationsTypes";

type UseReservationBoundsOptions = {
  userId?: string;
  bucketKey: string;
  appliedSearchVariables: ReservationSearch;
  fetchReservationBoundsQuery: LazyQueryExecFunction<
    GetVenueReservationBoundsQueryResponse,
    GetVenueReservationBoundsQueryInput
  >;
  updateBucket: (
    key: string,
    updater: (bucket: ReservationsBucket) => ReservationsBucket,
  ) => ReservationsBucket;
};

/**
 * Fetches and stores reservation bounds for the current search.
 */
export default function useReservationBounds({
  userId,
  bucketKey,
  appliedSearchVariables,
  fetchReservationBoundsQuery,
  updateBucket,
}: UseReservationBoundsOptions) {
  useEffect(() => {
    if (!userId) return;
    const key = bucketKey;
    let cancelled = false;
    fetchReservationBoundsQuery({
      variables: {
        venuePortalUserId: userId,
        ...appliedSearchVariables,
      },
    })
      .then((result) => {
        if (cancelled) return;
        const bounds = result.data?.venuePortalUser?.reservationBounds;
        updateBucket(key, (bucket) => {
          bucket.bounds = {
            earliestDate: bounds?.earliestDate
              ? new Date(bounds.earliestDate)
              : null,
            latestDate: bounds?.latestDate ? new Date(bounds.latestDate) : null,
            resolved: true,
          };
          return bucket;
        });
      })
      .catch(() => {
        if (cancelled) return;
      });
    return () => {
      cancelled = true;
    };
  }, [
    bucketKey,
    fetchReservationBoundsQuery,
    appliedSearchVariables,
    updateBucket,
    userId,
  ]);
}
